import type { Principal } from './client';
import type { QueryClient } from './zustandQuery';
import type { QueryClient as ServerQueryClient } from '@tanstack/react-query';

const STORAGE_KEY='mcp-page-previews-v1';
const MAX_BYTES=1_000_000,MAX_ENTRY_BYTES=150_000,TTL=5*60_000;
const PAGE_KEYS=new Set(['suppliers-providers','campaigns','dealz-control','livedealz-intake','livedealz-review',
  'team-operations','asset-library-builder','user-asset-libraries']);
const SHARED_PREFIXES=new Set(['campaigns','suppliers-providers','operations','governance']);
const PRIVATE_CONFIGURATION=/auth|secret|password|credential|token|permission|role|setting|audit|routing-polic|format-preset/i;
type Entry={port:'remote'|'server';key:readonly unknown[];data:unknown;at:number};
type Port={name:'remote';client:QueryClient}|{name:'server';client:ServerQueryClient};

export function principalReadIdentity(principal:Principal|null):string {
  return principal?JSON.stringify([principal.workspaceId,principal.id,principal.sessionId,principal.type,
    [...(principal.roles??[])].sort(),[...(principal.permissions??[])].sort(),principal.scope]):'';
}
function allowed(key:readonly unknown[]):boolean {
  // Native approval gates, editors, security/configuration and job status are
  // always authoritative reads. The existing progressive queue cache owns its
  // own complete/partial contracts; do not duplicate those snapshots here.
  if(key.some(part=>typeof part==='string'&&PRIVATE_CONFIGURATION.test(part)))return false;
  if(key[0]==='page')return PAGE_KEYS.has(String(key[1]));
  if(key[0]==='dashboard')return key[1]==='command-center-summary'||key[1]==='app-shell';
  return SHARED_PREFIXES.has(String(key[0]));
}
function safeJson(value:unknown,seen=new Set<object>(),depth=0):boolean {
  if(value===null||typeof value==='string'||typeof value==='boolean')return true;
  if(typeof value==='number')return Number.isFinite(value);
  if(!value||typeof value!=='object'||depth>25||seen.has(value))return false;
  if(!Array.isArray(value)&&Object.getPrototypeOf(value)!==Object.prototype)return false;
  seen.add(value);
  const safe=Object.entries(value).every(([key,child])=>
    (!/password|secret|credential|private[_-]?key|(?:access|refresh|csrf|api|auth|bearer)[_-]?token|^token$|api[_-]?key|^authorization$/i.test(key)||child==null||typeof child==='boolean')&&
    (child===undefined||safeJson(child,seen,depth+1)));
  seen.delete(value);return safe;
}
export function clearPageReloadCache(storage?:Storage) {
  try{(storage??window.sessionStorage).removeItem(STORAGE_KEY);}catch{/* Storage is optional. */}
}

/** Display previews are restored only after a fresh, verified principal. */
export function startPageReloadCache(client:QueryClient,server:ServerQueryClient,principal:Principal,storage?:Storage) {
  try{storage??=window.sessionStorage;}catch{return()=>{};}
  if(!principal.workspaceId||!principal.id||!principal.sessionId){clearPageReloadCache(storage);return()=>{};}
  const identity=principalReadIdentity(principal);
  const ports:Port[]=[{name:'remote',client},{name:'server',client:server}];
  const entries=new Map<string,Entry>();
  const fresh=(at:number)=>Number.isFinite(at)&&at<=Date.now()&&Date.now()-at<TTL;
  const id=(entry:Pick<Entry,'port'|'key'>)=>JSON.stringify([entry.port,entry.key]);
  try {
    const raw=storage.getItem(STORAGE_KEY);
    if(raw&&raw.length<=MAX_BYTES){
      const saved=JSON.parse(raw) as {identity?:unknown;entries?:Entry[]};
      if(saved.identity===identity&&Array.isArray(saved.entries))for(const entry of saved.entries.slice(-48)){
        const port=ports.find(port=>port.name===entry.port);
        if(!port||!Array.isArray(entry.key)||!allowed(entry.key)||!fresh(entry.at)||!safeJson(entry.data))continue;
        if(JSON.stringify(entry.data).length>MAX_ENTRY_BYTES)continue;
        const data=entry.key[0]==='dashboard'&&entry.key[1]==='app-shell'
          ? {...entry.data as object,principal}:entry.data;
        entries.set(id(entry),{...entry,data});
        if(port.name==='remote')port.client.setQueryData(entry.key,data,{updatedAt:entry.at});
        else port.client.setQueryData(entry.key,data,{updatedAt:entry.at});
        // A preview never extends freshness or supplies a publication decision.
        void port.client.invalidateQueries({queryKey:entry.key,exact:true,refetchType:'none'});
      }
    }
  } catch {/* Malformed storage cannot affect authentication. */}
  let timer:ReturnType<typeof setTimeout>|undefined;
  const save=()=>{
    if(timer)clearTimeout(timer);timer=undefined;
    for(const [key,entry]of entries)if(!fresh(entry.at))entries.delete(key);
    while(entries.size>48)entries.delete(entries.keys().next().value!);
    try{
      let raw=JSON.stringify({identity,entries:[...entries.values()]});
      while(raw.length>MAX_BYTES&&entries.size){entries.delete(entries.keys().next().value!);raw=JSON.stringify({identity,entries:[...entries.values()]});}
      if(entries.size)storage!.setItem(STORAGE_KEY,raw);else storage!.removeItem(STORAGE_KEY);
    }catch{clearPageReloadCache(storage);}
  };
  const schedule=()=>{if(!timer)timer=setTimeout(save,25);};
  save();
  const stops=ports.map(port=>port.client.getQueryCache().subscribe(event=>{
    const query=event.query;if(!query||!allowed(query.queryKey))return;
    const key=id({port:port.name,key:query.queryKey});
    if(event.type==='removed'||query.state.isInvalidated||query.state.status==='error'){
      entries.delete(key);schedule();return;
    }
    if(event.type!=='updated'||event.action?.type!=='success'||query.state.fetchStatus!=='idle'||
      !fresh(query.state.dataUpdatedAt)||ports.some(port=>port.client.isMutating()))return;
    const serialized=JSON.stringify(query.state.data);
    if(!serialized||serialized.length>MAX_ENTRY_BYTES||!safeJson(query.state.data))return;
    entries.delete(key);entries.set(key,{port:port.name,key:query.queryKey,data:query.state.data,at:query.state.dataUpdatedAt});schedule();
  }));
  const mutationStops=ports.map(port=>port.client.getMutationCache().subscribe(()=>{
    if(ports.some(port=>port.client.isMutating())){entries.clear();save();}
  }));
  if(typeof window!=='undefined')window.addEventListener('pagehide',save);
  return()=>{stops.forEach(stop=>stop());mutationStops.forEach(stop=>stop());save();
    if(typeof window!=='undefined')window.removeEventListener('pagehide',save);};
}
